import React, { useState, useEffect } from "react";
import { Card, Form, Input, Button, message, Modal, Row, Col, Badge, Popconfirm } from "antd";
import { CloudOutlined, GlobalOutlined, ApiOutlined, DeleteOutlined, SettingOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import axios from "axios";
import appConfig from "../../utils/config";

const IconMap = {
  CloudOutlined: <CloudOutlined style={{ fontSize: 24, color: "#F6821F" }} />,
  GlobalOutlined: <GlobalOutlined style={{ fontSize: 24, color: "#0765D9" }} />,
};

const Plugins = () => {
  const [form] = Form.useForm();
  const [catalog, setCatalog] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPlugin, setSelectedPlugin] = useState(null);

  const { accessToken } = useSelector((state) => state.auth);
  const token = accessToken || localStorage.getItem("accessToken");

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [catalogRes, installedRes] = await Promise.all([
        axios.get(`${appConfig.apiURL}/v2/plugins/catalog`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${appConfig.apiURL}/v2/plugins/installed`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (catalogRes.data?.status) {
        setCatalog(catalogRes.data.results);
      }
      if (installedRes.data?.status) {
        setInstalled(installedRes.data.results);
      }
    } catch (err) {
      console.error("Failed to fetch plugins details", err);
      message.error("Could not load plugin settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const openConfigModal = (plugin) => {
    setSelectedPlugin(plugin);
    const inst = installed.find((p) => p.pluginSlug === plugin.slug);
    
    if (inst) {
      // Pre-fill existing credentials
      form.setFieldsValue(inst.credentials || {});
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleUninstall = async (slug) => {
    if (!token) return;
    setActionLoading(true);
    try {
      const response = await axios.delete(`${appConfig.apiURL}/v2/plugins/${slug}/uninstall`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status) {
        message.success("Plugin uninstalled successfully!");
        fetchData();
      } else {
        message.error("Failed to uninstall plugin.");
      }
    } catch (err) {
      console.error("Uninstall error", err);
      message.error(err.response?.data?.message || "Error uninstalling plugin.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleModalSubmit = async (values) => {
    if (!token || !selectedPlugin) return;
    setActionLoading(true);
    
    const isInstalled = installed.some((p) => p.pluginSlug === selectedPlugin.slug);
    const url = isInstalled 
      ? `${appConfig.apiURL}/v2/plugins/${selectedPlugin.slug}/credentials`
      : `${appConfig.apiURL}/v2/plugins/install`;
      
    const payload = isInstalled 
      ? { credentials: values }
      : { pluginSlug: selectedPlugin.slug, credentials: values };

    try {
      const response = isInstalled 
        ? await axios.put(url, payload, { headers: { Authorization: `Bearer ${token}` } })
        : await axios.post(url, payload, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data?.status) {
        message.success(`${selectedPlugin.name} configured successfully!`);
        setModalVisible(false);
        fetchData();
      } else {
        message.error("Failed to save configuration.");
      }
    } catch (err) {
      console.error("Configuration save error", err);
      message.error(err.response?.data?.message || "Error saving configuration.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">Plugin Integrations</h3>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="/">Dashboard</a>
                </li>
                <li className="breadcrumb-item active">Plugins</li>
              </ul>
            </div>
          </div>
        </div>

        <Row gutter={[24, 24]}>
          {catalog.map((plugin) => {
            const inst = installed.find((p) => p.pluginSlug === plugin.slug);
            const isInstalled = !!inst;

            return (
              <Col xs={24} sm={24} md={12} lg={12} xl={8} key={plugin.slug}>
                <Badge.Ribbon 
                  text={isInstalled ? "Installed" : "Available"} 
                  color={isInstalled ? "green" : "gray"}
                >
                  <Card
                    hoverable
                    loading={loading}
                    title={
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {IconMap[plugin.icon] || <ApiOutlined style={{ fontSize: 24, color: plugin.color }} />}
                        <span>{plugin.name}</span>
                      </div>
                    }
                    actions={[
                      isInstalled ? (
                        <Button 
                          type="link" 
                          icon={<SettingOutlined />} 
                          onClick={() => openConfigModal(plugin)}
                        >
                          Configure
                        </Button>
                      ) : (
                        <Button 
                          type="link" 
                          icon={<PlusOutlined />} 
                          onClick={() => openConfigModal(plugin)}
                        >
                          Install
                        </Button>
                      ),
                      isInstalled && (
                        <Popconfirm
                          title="Are you sure you want to uninstall this plugin?"
                          description="This will clear your credentials and disable dependent operations."
                          onConfirm={() => handleUninstall(plugin.slug)}
                          okText="Yes"
                          cancelText="No"
                          disabled={actionLoading}
                        >
                          <Button 
                            type="link" 
                            danger 
                            icon={<DeleteOutlined />}
                          >
                            Uninstall
                          </Button>
                        </Popconfirm>
                      ),
                    ].filter(Boolean)}
                  >
                    <div style={{ minHeight: 70, color: "#666" }}>
                      {plugin.description}
                    </div>
                  </Card>
                </Badge.Ribbon>
              </Col>
            );
          })}
        </Row>

        {selectedPlugin && (
          <Modal
            title={`Configure ${selectedPlugin.name}`}
            open={modalVisible}
            onCancel={() => setModalVisible(false)}
            footer={null}
            destroyOnClose
          >
            <div style={{ marginBottom: 20, color: "#888" }}>
              Provide settings for {selectedPlugin.name}. Credentials are encrypted and persisted securely.
            </div>
            
            <Form form={form} layout="vertical" onFinish={handleModalSubmit}>
              {selectedPlugin.credentialFields.map((field) => (
                <Form.Item
                  key={field.key}
                  label={field.label}
                  name={field.key}
                  rules={[
                    {
                      required: field.required,
                      message: `Please input ${field.label}!`,
                    },
                  ]}
                >
                  {field.type === "password" ? (
                    <Input.Password placeholder={field.placeholder} />
                  ) : (
                    <Input placeholder={field.placeholder} />
                  )}
                </Form.Item>
              ))}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
                <Button onClick={() => setModalVisible(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={actionLoading}>
                  Save Settings
                </Button>
              </div>
            </Form>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default Plugins;
